import { useEffect, useRef, useState } from "react";
import { Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, imgUrl } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { LANGS } from "@/i18n/strings";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const EMPTY_I18N = { ar: "", fr: "", en: "", it: "", de: "" };
export const emptyProduct = () => ({
  name: { ...EMPTY_I18N }, description: { ...EMPTY_I18N }, family: { ...EMPTY_I18N },
  notes_top: "", notes_heart: "", notes_base: "", volume: "", price: "", compare_price: "", stock: "", images: [], active: true, featured: false, sort: 0,
});

const inp = "mt-1 w-full border border-[#241914]/20 bg-white/60 px-3 py-2.5 text-sm outline-none focus:border-[#C6A76A]";

function L({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs text-[#241914]/75">{label}</span>
      {children}
    </label>
  );
}

export function ProductForm({ initial, onClose, onSaved }) {
  const { t } = useLang();
  const [p, setP] = useState(initial);
  const [tab, setTab] = useState("ar");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    setP(initial);
  }, [initial]);

  const setI18n = (field, v) => setP((x) => ({ ...x, [field]: { ...x[field], [tab]: v } }));
  const set = (k) => (e) => setP((x) => ({ ...x, [k]: e.target.value }));

  const upload = async (files) => {
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/upload", fd);
        setP((x) => ({ ...x, images: [...x.images, data.path] }));
      }
    } catch (e) {
      toast.error(errMsg(e));
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const makeCover = (i) => setP((x) => ({ ...x, images: [x.images[i], ...x.images.filter((_, j) => j !== i)] }));

  const save = async () => {
    setSaving(true);
    const body = {
      ...p,
      price: Number(p.price),
      compare_price: p.compare_price === "" || p.compare_price == null ? null : Number(p.compare_price),
      stock: parseInt(p.stock || 0, 10),
      sort: parseInt(p.sort || 0, 10),
    };
    try {
      if (p.id) await api.put(`/admin/products/${p.id}`, body);
      else await api.post("/admin/products", body);
      toast.success(t("a_saved"));
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="admin-product-form" className="max-h-[92vh] max-w-3xl overflow-y-auto rounded-none border-[#C6A76A]/30 bg-[#F7F3EB] p-6 sm:p-8">
        <DialogTitle className="font-display text-3xl font-normal">{p.id ? t("a_edit_product") : t("a_add_product")}</DialogTitle>

        <div className="mt-2 flex flex-wrap gap-1 border-b border-[#241914]/15" data-testid="admin-product-lang-tabs">
          {LANGS.map((l) => (
            <button key={l.code} type="button" data-testid={`admin-product-lang-${l.code}`} onClick={() => setTab(l.code)} className={`px-3 py-2 text-xs ${tab === l.code ? "border-b-2 border-[#C6A76A] text-[#0B0B0C]" : "text-[#241914]/60"}`}>
              {l.label} {p.name[l.code] ? "•" : ""}
            </button>
          ))}
        </div>
        <p className="text-xs text-[#241914]/65">{t("a_lang_hint")}</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" dir={LANGS.find((l) => l.code === tab)?.dir}>
          <L label={`${t("a_name")} (${tab})`}><input data-testid="admin-product-name-input" className={inp} value={p.name[tab]} onChange={(e) => setI18n("name", e.target.value)} /></L>
          <L label={`${t("a_family")} (${tab})`}><input data-testid="admin-product-family-input" className={inp} value={p.family[tab]} onChange={(e) => setI18n("family", e.target.value)} /></L>
          <L label={`${t("a_description")} (${tab})`} className="sm:col-span-2"><textarea data-testid="admin-product-description-input" rows={4} className={inp} value={p.description[tab]} onChange={(e) => setI18n("description", e.target.value)} /></L>
        </div>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <L label={t("a_price")}><input data-testid="admin-product-price-input" type="number" min="0" step="0.01" className={inp} value={p.price} onChange={set("price")} dir="ltr" /></L>
          <L label={t("a_compare_price")}><input data-testid="admin-product-compare-input" type="number" min="0" step="0.01" className={inp} value={p.compare_price ?? ""} onChange={set("compare_price")} dir="ltr" /></L>
          <L label={t("a_stock")}><input data-testid="admin-product-stock-input" type="number" min="0" className={inp} value={p.stock} onChange={set("stock")} dir="ltr" /></L>
          <L label={t("a_volume")}><input data-testid="admin-product-volume-input" className={inp} value={p.volume} onChange={set("volume")} dir="ltr" /></L>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <L label={t("notes_top")}><input data-testid="admin-product-notes-top" className={inp} value={p.notes_top} onChange={set("notes_top")} /></L>
          <L label={t("notes_heart")}><input data-testid="admin-product-notes-heart" className={inp} value={p.notes_heart} onChange={set("notes_heart")} /></L>
          <L label={t("notes_base")}><input data-testid="admin-product-notes-base" className={inp} value={p.notes_base} onChange={set("notes_base")} /></L>
        </div>

        <div>
          <p className="text-xs text-[#241914]/75">{t("a_images")}</p>
          <div className="mt-2 flex flex-wrap gap-3">
            {p.images.map((im, i) => (
              <div key={im} className="group relative h-28 w-24 overflow-hidden border border-[#241914]/15 bg-white" data-testid={`admin-product-image-${i}`}>
                <img src={imgUrl(im)} alt="" className="h-full w-full object-cover" />
                {i === 0 ? (
                  <span className="absolute inset-x-0 bottom-0 bg-[#0B0B0C]/80 py-0.5 text-center text-[0.6rem] text-[#C6A76A]">{t("a_cover")}</span>
                ) : (
                  <button type="button" onClick={() => makeCover(i)} className="absolute inset-x-0 bottom-0 bg-[#0B0B0C]/70 py-0.5 text-[0.6rem] text-white opacity-0 group-hover:opacity-100">{t("a_cover")}</button>
                )}
                <button type="button" data-testid={`admin-product-image-remove-${i}`} onClick={() => setP((x) => ({ ...x, images: x.images.filter((_, j) => j !== i) }))} className="absolute end-1 top-1 bg-[#0B0B0C]/80 p-1 text-white"><X className="h-3 w-3" /></button>
              </div>
            ))}
            <button type="button" data-testid="admin-product-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading} className="flex h-28 w-24 flex-col items-center justify-center gap-2 border border-dashed border-[#241914]/30 text-xs text-[#241914]/70 hover:border-[#C6A76A]">
              {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" strokeWidth={1.3} />}
              {uploading ? t("a_uploading") : t("a_upload")}
            </button>
            <input ref={fileRef} data-testid="admin-product-file-input" type="file" accept="image/jpeg,image/png,image/webp,image/avif" multiple hidden onChange={(e) => e.target.files?.length && upload(e.target.files)} />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-8">
          <label className="flex items-center gap-3 text-sm"><Switch data-testid="admin-product-active-switch" checked={p.active} onCheckedChange={(v) => setP((x) => ({ ...x, active: v }))} /> {t("a_active")}</label>
          <label className="flex items-center gap-3 text-sm"><Switch data-testid="admin-product-featured-switch" checked={p.featured} onCheckedChange={(v) => setP((x) => ({ ...x, featured: v }))} /> {t("a_featured")}</label>
          <L label={t("a_sort")}><input type="number" className={`${inp} w-24`} value={p.sort} onChange={set("sort")} dir="ltr" /></L>
        </div>

        <div className="flex justify-end gap-3 border-t border-[#241914]/15 pt-6">
          <button type="button" onClick={onClose} className="px-6 py-3 text-sm" data-testid="admin-product-cancel-btn">{t("a_cancel")}</button>
          <button type="button" data-testid="admin-product-save-btn" onClick={save} disabled={saving || uploading} className="flex items-center gap-2 bg-[#0B0B0C] px-8 py-3 text-sm text-[#F7F3EB] hover:bg-[#241914] disabled:opacity-50">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} {t("a_save")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
